import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Paper, Typography, Button, Breadcrumbs, Menu, MenuItem } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import DataTable from "datatables.net-bs5";
import $ from "jquery";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import "select2/dist/css/select2.min.css";
import "select2";
import Swal from "sweetalert2";
import "./RaiseComplaint.css";
import "./BusinessVerticalMasterManagement.css";
import { componentMasterAPI } from "../services/api";
import { getRoutePermissions } from "../utils/auth";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getDataTableExportRows } from "../utils/dataTableExportUtils";

const normalizeComponent = (item) => {
  return {
    componentCode: item.componentCode || item.ComponentCode || item.COMPONANTCODE || "",
    componentId: item.componentId || item.ComponentId || item.COMPONANTID || "",
    componentName: item.componentName || item.ComponentName || item.COMPONANTNAME || "",
    componentCatgCode: item.componentCatgCode || item.ComponentCatgCode || item.COMPONANTCATGCODE || "",
    componentCategoryName: item.componentCategoryName || item.ComponentCategoryName || "",
    bvCode: item.bvCode || item.BVCode || item.BVCODE || "",
    businessVerticalName: item.businessVerticalName || item.BusinessVerticalName || "",
    isActive: item.isActive ?? item.IsActive ?? false,
    createdOn: item.createdOn || item.CreatedOn || null,
  };
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

const ComponentMasterManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
  });
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNumber] = useState(1);
  const [pageSize] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const tableRef = useRef(null);
  const dataTableInstanceRef = useRef(null);
  const statusSelectRef = useRef(null);

  const routePermissions = getRoutePermissions(location.pathname);
  const { canView, canAdd, canEdit } = routePermissions;

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  useEffect(() => {
    if (!canView) {
      Swal.fire({
        title: "Access Denied",
        text: "You do not have permission to view this page.",
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => navigate("/dashboard"));
      return;
    }
    fetchComponents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  // Initialize Select2 for Status dropdown
  useEffect(() => {
    if (statusSelectRef.current) {
      const $select = $(statusSelectRef.current);

      if ($select.data("select2")) {
        $select.select2("destroy");
      }

      $select.select2({
        width: "100%",
        minimumResultsForSearch: Infinity,
      });

      $select.on("change", function (e) {
        handleFilterChange("status", e.target.value);
      });

      return () => {
        if ($select.data("select2")) {
          $select.select2("destroy");
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update Select2 value when filters.status changes
  useEffect(() => {
    if (statusSelectRef.current && $(statusSelectRef.current).data("select2")) {
      const $select = $(statusSelectRef.current);
      const currentValue = $select.val();
      if (currentValue !== filters.status) {
        $select.off("change");
        $select.val(filters.status).trigger("change.select2");
        $select.on("change", function (e) {
          handleFilterChange("status", e.target.value);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status]);

  useEffect(() => {
    if (!tableRef.current) return;

    // If table already exists, update it with new data
    if (dataTableInstanceRef.current) {
      try {
        dataTableInstanceRef.current.clear();
        dataTableInstanceRef.current.rows.add(components);
        dataTableInstanceRef.current.draw();
        console.log("DataTable updated with", components.length, "rows");
        return;
      } catch (e) {
        console.log("Error updating table, will recreate:", e);
        dataTableInstanceRef.current.destroy();
        dataTableInstanceRef.current = null;
      }
    }

    // Only initialize if we have data or if we want to show empty state
    console.log("Initializing DataTable with data:", components);

    try {
      if (!$.fn.DataTable) {
        DataTable($);
      }

      dataTableInstanceRef.current = $(tableRef.current).DataTable({
        data: components,
        paging: true,
        searching: true,
        info: true,
        lengthChange: true,
        lengthMenu: [5, 10, 25, 50, 100],
        dom: "lfrtip",
        ordering: true,
        destroy: true,
        scrollX: true,
        autoWidth: false,
        columns: [
          {
            title: "SNo.",
            data: null,
            render: (d, type, row, meta) =>
              (pageNumber - 1) * pageSize + meta.row + 1,
            className: "text-center",
          },
          {
            title: "Component ID",
            data: "componentId",
            defaultContent: "-",
            className: "text-center",
          },
          { 
            title: "Component Code", 
            data: "componentCode",
            visible: false
          },
          { title: "Component Name", data: "componentName" },
          {
            title: "Component Category",
            data: "componentCategoryName",
            render: (d) => d || "-",
          },
          {
            title: "Business Vertical",
            data: "businessVerticalName",
            render: (d) => d || "-",
          },
          {
            title: "Created On",
            data: "createdOn",
            render: (d) => formatDateTime(d),
          },
          {
            title: "Status",
            data: null,
            orderable: false,
            render: (d) => {
              const isActive = d.isActive === true || d.isActive === 1;
              const toggleClass = isActive ? "active" : "inactive";
              const toggleIcon = isActive ? "bi-toggle-on" : "bi-toggle-off";
              if (!canEdit) {
                return `<div class="d-flex justify-content-center">
                  <span title="${isActive ? "Active" : "Inactive"}" style="background:transparent;border:none;padding:0;font-size:24px;color:${isActive ? "#4caf50" : "#9e9e9e"};cursor:default;">
                    <i class="bi ${toggleIcon}"></i>
                  </span>
                </div>`;
              }
              return `<div class="d-flex justify-content-center">
                <button class="btn btn-sm status-toggle ${toggleClass}" 
                  data-code="${d.componentCode}" 
                  data-active="${isActive}"
                  title="${
                    isActive ? "Click to deactivate" : "Click to activate"
                  }"
                  style="background: transparent; 
                         border: none; 
                         padding: 0;
                         font-size: 24px;
                         color: ${isActive ? "#4caf50" : "#9e9e9e"};
                         cursor: pointer;
                         transition: all 0.3s ease;">
                  <i class="bi ${toggleIcon}"></i>
                </button>
              </div>`;
            },
          },
          // {
          //   title: "Action",
          //   data: null,
          //   orderable: false,
          //   searchable: false,
          //   render: (d) =>
          //     `<div class="d-flex justify-content-center gap-2">
          //       <button class="btn btn-sm edit-component" data-code="${d.componentCode}" title="Edit Component"
          //         style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); 
          //                border: none; 
          //                color: white; 
          //                padding: 8px 10px; 
          //                border-radius: 6px; 
          //                box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
          //                transition: all 0.3s ease;
          //                font-weight: 500;
          //                display: flex;
          //                align-items: center;
          //                justify-content: center;
          //                min-width: 36px;
          //                height: 36px;">
          //         <i class="bi bi-pencil-fill"></i>
          //       </button>
          //       <button class="btn btn-sm delete-component" data-code="${d.componentCode}" title="Delete Component"
          //         style="background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); 
          //                border: none; 
          //                color: white; 
          //                padding: 8px 10px; 
          //                border-radius: 6px; 
          //                box-shadow: 0 2px 8px rgba(245, 87, 108, 0.4);
          //                transition: all 0.3s ease;
          //                font-weight: 500;
          //                display: flex;
          //                align-items: center;
          //                justify-content: center;
          //                min-width: 36px;
          //                height: 36px;">
          //         <i class="bi bi-trash-fill"></i>
          //       </button>
          //     </div>`,
          // },
        ],
        initComplete: function () {
          try {
            const wrapper = $(tableRef.current).closest(
              ".dataTables_wrapper, .dt-container"
            );
            if (wrapper.length > 0) {
              const lengthEl = wrapper.find(".dt-length");
              const searchEl = wrapper.find(".dt-search");
              if (lengthEl.length > 0 || searchEl.length > 0) {
                let topWrap = wrapper.find(".dtFltrTop");
                if (topWrap.length === 0) {
                  topWrap = $("<div class='dtFltrTop d-flex flex-wrap'></div>");
                  wrapper.prepend(topWrap);
                }
                lengthEl.each(function () {
                  $(this).appendTo(topWrap);
                });
                searchEl.each(function () {
                  $(this).appendTo(topWrap);
                });
              }

              const infoEl = wrapper.find(".dt-info");
              const pagingEl = wrapper.find(".dt-paging");
              if (infoEl.length > 0 || pagingEl.length > 0) {
                let bottomWrap = wrapper.find(".dtFltrBotm");
                if (bottomWrap.length === 0) {
                  bottomWrap = $(
                    "<div class='dtFltrBotm d-flex flex-wrap'></div>"
                  );
                  wrapper.append(bottomWrap);
                }
                infoEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
                pagingEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
              }
            }
          } catch (error) {
            console.error("Error in initComplete:", error);
          }
        },
      });

      // $(tableRef.current)
      //   .off("click", ".edit-component")
      //   .on("click", ".edit-component", function () {
      //     const code = $(this).data("code");
      //     if (code) navigate(`/components/edit/${code}`);
      //   });

      // $(tableRef.current)
      //   .off("click", ".delete-component")
      //   .on("click", ".delete-component", function () {
      //     const code = $(this).data("code");
      //     if (code) handleDelete(code);
      //   });

      if (canEdit) {
        $(tableRef.current)
          .off("click", ".status-toggle")
          .on("click", ".status-toggle", function () {
            const code = $(this).data("code");
            const currentActive =
              $(this).data("active") === true ||
              $(this).data("active") === "true" ||
              $(this).data("active") === 1;
            handleStatusToggle(code, !currentActive);
          });
      } else {
        $(tableRef.current).off("click", ".status-toggle");
      }
    } catch (err) {
      console.error("DataTable init error", err);
    }

    return () => {
      if (dataTableInstanceRef.current) {
        dataTableInstanceRef.current.destroy();
        dataTableInstanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [components, canEdit]);

  const fetchComponents = async (
    search = filters.search,
    status = filters.status
  ) => {
    setLoading(true);
    try {
      const response = await componentMasterAPI.getPaged({
        searchText: search || undefined,
        status: status === "all" ? undefined : status,
        pageNumber: pageNumber,
        pageSize: pageSize,
      });

      console.log("API Response:", response);
      
      if (response?.success && response.data) {
        const payload = response.data;
        const items = payload.components || payload.Components || [];
        console.log("Raw items from API:", items);
        
        const normalizedItems = items.map((comp) => normalizeComponent(comp));
        
        console.log("Normalized items:", normalizedItems);
        setComponents(normalizedItems);
        setTotalRecords(payload.totalRecords || payload.TotalRecords || 0);
      } else {
        console.warn("No data in response:", response);
        setComponents([]);
        setTotalRecords(0);
      }
    } catch (e) {
      console.error("Error fetching components:", e);
      setComponents([]);
      Swal.fire(
        "Error",
        "Failed to load components. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({ search: "", status: "all" });
    fetchComponents("", "all");
  };

  const handleStatusToggle = async (componentCode, newActive) => {
    const actionText = newActive ? "activate" : "deactivate";
    const result = await Swal.fire({
      title: "Confirm Status Change",
      text: `Are you sure you want to ${actionText} this component?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: newActive ? "#4caf50" : "#ff9800",
      cancelButtonColor: "#6c757d",
      confirmButtonText: newActive ? "Yes, activate!" : "Yes, deactivate!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        const modifiedBy = currentUser?.userIdCode || "SYSADMIN001";
        const response = await componentMasterAPI.updateStatus(
          componentCode,
          newActive,
          modifiedBy
        );

        if (response?.success) {
          Swal.fire(
            "Success!",
            response.message || `Component has been ${actionText}d.`,
            "success"
          );
          fetchComponents();
        } else {
          Swal.fire(
            "Error",
            response?.message || `Failed to ${actionText} component`,
            "error"
          );
        }
      } catch (e) {
        console.error(`Error ${actionText}ing component:`, e);
        Swal.fire(
          "Error",
          e.response?.data?.message ||
            `Failed to ${actionText} component. Please try again.`,
          "error"
        );
      }
    }
  };

  const handleDelete = async (componentCode) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will deactivate the component.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, proceed!",
    });

    if (result.isConfirmed) {
      try {
        const modifiedBy = currentUser?.userIdCode || "SYSADMIN001";
        const response = await componentMasterAPI.delete(componentCode, modifiedBy);

        if (response?.success) {
          Swal.fire(
            "Updated!",
            response.message || "Component has been deactivated.",
            "success"
          );
          fetchComponents();
        } else {
          Swal.fire(
            "Error",
            response?.message || "Failed to update component",
            "error"
          );
        }
      } catch (e) {
        console.error("Error deleting component:", e);
        Swal.fire(
          "Error",
          e.response?.data?.message ||
            "Failed to update component. Please try again.",
          "error"
        );
      }
    }
  };

  // Export to PDF
  const exportToPDF = () => {
    // Export what the grid is actually showing: when a search is active
    // only the matching rows are written out, otherwise the full data set.
    const exportRows = getDataTableExportRows(dataTableInstanceRef, components);
    try {
      if (!exportRows || exportRows.length === 0) {
        Swal.fire("Info", "No data to export", "info");
        return;
      }

      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text("Component Master Data", 14, 15);

      const tableData = exportRows.map((component, index) => [
        String(index + 1),
        String(component.componentId || "-"),
        String(component.componentName || "-"),
        String(component.componentCategoryName || "-"),
        String(component.businessVerticalName || "-"),
        String(formatDateTime(component.createdOn) || "-"),
        component.isActive ? "Active" : "Inactive",
      ]);

      // Use autoTable function (imported directly)
      autoTable(doc, {
        head: [["SNo.", "Component ID", "Component Name", "Component Category", "Business Vertical", "Created On", "Status"]],
        body: tableData,
        startY: 25,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [102, 126, 234] },
        margin: { top: 25 },
      });

      doc.save(`Component_Master_${new Date().toISOString().split("T")[0]}.pdf`);
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      Swal.fire({
        title: "Error",
        text: `Failed to export PDF: ${error.message || "Unknown error"}. Please check the browser console for details.`,
        icon: "error",
      });
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    // Export what the grid is actually showing: when a search is active
    // only the matching rows are written out, otherwise the full data set.
    const exportRows = getDataTableExportRows(dataTableInstanceRef, components);
    try {
      const headers = ["SNo.", "Component ID", "Component Name", "Component Category", "Business Vertical", "Created On", "Status"];
      const csvData = exportRows.map((component, index) => [
        index + 1,
        component.componentId || "",
        component.componentName || "",
        component.componentCategoryName || "",
        component.businessVerticalName || "",
        formatDateTime(component.createdOn),
        component.isActive ? "Active" : "Inactive",
      ]);

      const csvContent = [
        headers.join(","),
        ...csvData.map((row) => row.map((cell) => `"${cell}"`).join(",")),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `Component_Master_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting to CSV:", error);
      Swal.fire("Error", "Failed to export CSV", "error");
    }
  };

  // Export to Excel
  const exportToExcel = async () => {
    // Export what the grid is actually showing: when a search is active
    // only the matching rows are written out, otherwise the full data set.
    const exportRows = getDataTableExportRows(dataTableInstanceRef, components);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Component Master");

      // Add title
      worksheet.mergeCells("A1:G1");
      const titleRow = worksheet.getRow(1);
      titleRow.getCell(1).value = "Component Master Data";
      titleRow.getCell(1).font = { size: 16, bold: true };
      titleRow.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
      titleRow.height = 25;

      // Add headers
      const headerRow = worksheet.getRow(3);
      headerRow.values = ["SNo.", "Component ID", "Component Name", "Component Category", "Business Vertical", "Created On", "Status"];
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF667EEA" },
      };
      headerRow.alignment = { horizontal: "center", vertical: "middle" };
      headerRow.height = 20;

      // Add data
      exportRows.forEach((component, index) => {
        const row = worksheet.addRow([
          index + 1,
          component.componentId || "",
          component.componentName || "",
          component.componentCategoryName || "",
          component.businessVerticalName || "",
          formatDateTime(component.createdOn),
          component.isActive ? "Active" : "Inactive",
        ]);
        row.alignment = { vertical: "middle" };
      });

      // Set column widths
      worksheet.columns.forEach((column) => {
        column.width = 20;
      });

      // Generate buffer and download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `Component_Master_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      Swal.fire("Error", "Failed to export Excel", "error");
    }
  };

  // Handle export menu
  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportOption = (format) => {
    handleExportMenuClose();
    if (format === "excel") {
      exportToExcel();
    } else if (format === "csv") {
      exportToCSV();
    } else if (format === "pdf") {
      exportToPDF();
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Breadcrumbs
            aria-label="breadcrumb"
            className="add-edit-product-breadcrumb"
          >
            <Typography
              color="text.primary"
              className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
            >
              Component Master
            </Typography>
          </Breadcrumbs>
          <Typography variant="body2" sx={{ color: "#666666", mt: 1 }}>
            Manage component master.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          {/* Export Button with Dropdown */}
          <Button
            onClick={handleExportMenuOpen}
            sx={{
              background: "transparent",
              border: "1px solid #667eea",
              color: "#667eea",
              fontWeight: 500,
              padding: "8px 10px",
              borderRadius: "6px",
              transition: "all 0.3s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "36px",
              height: "36px",
              "&:hover": {
                background: "transparent",
                borderColor: "#764ba2",
                color: "#764ba2",
              },
              "& .MuiSvgIcon-root": {
                color: "#667eea",
              },
              "&:hover .MuiSvgIcon-root": {
                color: "#764ba2",
              },
            }}
            title="Export"
          >
            <FileDownloadIcon sx={{ fontSize: "26px", filter: "drop-shadow(0 0 0.5px currentColor)" }} />
            <ArrowDropDownIcon sx={{ fontSize: "16px", ml: 0.5 }} />
          </Button>
          <Menu
            anchorEl={exportMenuAnchor}
            open={Boolean(exportMenuAnchor)}
            onClose={handleExportMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <MenuItem onClick={() => handleExportOption("excel")}>
              Excel
            </MenuItem>
            <MenuItem onClick={() => handleExportOption("csv")}>
              CSV
            </MenuItem>
            <MenuItem onClick={() => handleExportOption("pdf")}>
              PDF
            </MenuItem>
          </Menu>
          {canAdd && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/components/add")}
              sx={{
                background: "linear-gradient(135deg, #dc3545 0%, #ff0022 100%)",
                border: "none",
                color: "white",
                fontWeight: 600,
                fontSize: "11px",
                boxShadow: "0 4px 15px rgba(245, 87, 108, 0.4)",
                borderRadius: "4px",
                textTransform: "uppercase",
                padding: "8px 24px",
                "&:hover": {
                  background: "linear-gradient(135deg, #dc3545 0%, #ff0022 100%)",
                  boxShadow: "0 6px 20px rgba(245, 87, 108, 0.6)",
                  transform: "translateY(-2px)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Add Component
            </Button>
          )}
        </Box>
      </Box>

      {/* Filter Panel */}
      <div className="card-panel mb-3 filter-panel">
        <div
          className="section-heading d-flex align-items-center gap-2 mb-3"
          style={{ marginBottom: "14px" }}
        >
          <SearchIcon
            fontSize="small"
            className="text-muted"
            style={{
              width: "18px",
              height: "18px",
              flexShrink: 0,
              marginTop: "2px",
            }}
          />
          <span>Filter: Name and Status</span>
        </div>

        <div className="container-fluid">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-6 col-lg-4 srchDvwrpr">
              <label className="input-label d-block mb-1 text-start">
                Search
              </label>
              <div className="input-icon" style={{ position: "relative" }}>
                <input
                  type="text"
                  className="form-control form-control-light text-start"
                  placeholder="Search by Code / ID / Name"
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleFilterChange("search", filters.search.trim());
                      fetchComponents(
                        filters.search.trim(),
                        filters.status
                      );
                    }
                  }}
                  style={{ height: "42px", textAlign: "left" }}
                />
                <SearchIcon
                  className="icon-right"
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                    width: "16px",
                    height: "16px",
                    color: "#6c757d",
                  }}
                />
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label d-block mb-1 text-start">
                Status
              </label>
              <select
                ref={statusSelectRef}
                id="status-filter-select"
                className="form-select form-control-light filter-select text-start"
                value={filters.status}
                style={{ height: "42px", textAlign: "left" }}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <label
                className="input-label d-block mb-1 text-start"
                style={{ visibility: "hidden" }}
              >
                Actions
              </label>
              <div className="d-flex gap-2 filter-buttons justify-content-start">
                <button
                  type="button"
                  className="btn btn-filter"
                  onClick={() => {
                    handleFilterChange("search", filters.search.trim());
                    fetchComponents(
                      filters.search.trim(),
                      filters.status
                    );
                  }}
                  style={{
                    background:
                      "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)",
                    border: "none",
                    color: "white",
                    fontWeight: 600,
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                    borderRadius: "4px",
                    padding: "8px 24px",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #0f00f5 0%, #0f00f5 100%)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(102, 126, 234, 0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 15px rgba(102, 126, 234, 0.4)";
                  }}
                >
                  <SearchIcon fontSize="small" className="me-1" />
                  Search
                </button>
                <button
                  type="button"
                  className="btn btn-reset-filter"
                  onClick={resetFilters}
                  title="Reset"
                  style={{
                    background:
                      "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
                    border: "none",
                    color: "white",
                    fontWeight: 600,
                    boxShadow: "0 4px 15px rgba(132, 250, 176, 0.4)",
                    borderRadius: "4px",
                    padding: "8px 16px",
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <RestartAltIcon fontSize="small" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Paper
        sx={{
          p: 0,
          backgroundColor: "transparent",
          boxShadow: "none",
          border: "none",
        }}
      >
        <div className="card-panel mt-0">
          {loading && components.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography>Loading components...</Typography>
            </Box>
          ) : components.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No components found. Click "Add Component" to create a new component.
              </Typography>
            </Box>
          ) : (
            <div className="table-responsive">
              <table
                ref={tableRef}
                className="table table-striped table-bordered complaint-table custmTblLst"
                style={{ width: "100%" }}
              />
            </div>
          )}
        </div>
      </Paper>
    </Box>
  );
};

export default ComponentMasterManagement;
